import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import Section from '../components/Section.jsx';
import PricingCalculator from './PricingCalculator.jsx';
import { fetchPlans } from '../api.js';
import { config } from '../config.js';

const formatBRLInt = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(value ?? 0));

/**
 * Seção de precificação — modelo de faixas regressivas.
 *
 * Os dados vêm da API (GET /plans) para não haver divergência entre a landing
 * e a regra de cobrança. Se a API não responder, mostra os valores hardcoded
 * como fallback (nunca deixa a seção vazia).
 */
export default function Plans() {
  const [pricing, setPricing] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchPlans({ signal: controller.signal })
      .then((data) => setPricing(data.pricing ?? null))
      .catch((err) => {
        if (err.name !== 'AbortError') setError(err.message);
      });
    return () => controller.abort();
  }, []);

  // Fallback se a API não responder
  const basePriceBRL = pricing?.basePriceBRL ?? 50;
  const trialDays = pricing?.trialDays ?? 30;

  return (
    <Section
      id="planos"
      eyebrow="Planos para fornecedores"
      title="Quanto mais você cresce, menos paga por morador"
      subtitle="Mensalidade fixa de R$\u00A050 + um valor por morador alcançado que diminui conforme você escala. Os primeiros 30 dias são por nossa conta."
    >
      {error && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Não foi possível carregar os valores atualizados. Os valores abaixo são de referência.
        </Alert>
      )}

      {/* Destaques */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={0} sx={{ height: '100%', textAlign: 'center', p: 3 }}>
            <Typography variant="h3" color="secondary.main" sx={{ fontWeight: 700 }}>
              {formatBRLInt(basePriceBRL)}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Mensalidade fixa da plataforma
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Independente do número de anúncios
            </Typography>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={0} sx={{ height: '100%', textAlign: 'center', p: 3 }}>
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', alignItems: 'center' }}>
              <TrendingDownIcon color="success" fontSize="large" />
              <Typography variant="h3" color="success.main" sx={{ fontWeight: 700 }}>
                Regressivo
              </Typography>
            </Stack>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Custo por morador diminui a cada faixa
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Quanto mais condomínios, mais barato fica
            </Typography>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={0} sx={{ height: '100%', textAlign: 'center', p: 3 }}>
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', alignItems: 'center' }}>
              <CardGiftcardIcon color="secondary" fontSize="large" />
              <Typography variant="h3" color="secondary.main" sx={{ fontWeight: 700 }}>
                {trialDays} dias
              </Typography>
            </Stack>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Grátis para experimentar
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Sem compromisso, cancele quando quiser
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Calculadora interativa */}
      <Box sx={{ mb: 5 }}>
        <PricingCalculator />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* O que está incluído */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          'Perfil completo do fornecedor',
          'Anúncios ilimitados',
          'Pacotes de vouchers',
          'Agenda de serviços',
          'Adesão a múltiplos condomínios',
          'Destaque no feed ao escalar',
        ].map((feature) => (
          <Grid key={feature} size={{ xs: 12, sm: 6, md: 4 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <CheckCircleOutlinedIcon fontSize="small" sx={{ color: 'success.main' }} />
              <Typography variant="body2">{feature}</Typography>
            </Stack>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ textAlign: 'center' }}>
        <Button href={config.appUrl} variant="contained" color="secondary" size="large">
          Criar perfil de fornecedor
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Moradores, síndicos e condomínios não pagam nada. Fornecedores ganham {trialDays} dias
          grátis ao aderir.
        </Typography>
      </Box>
    </Section>
  );
}
